import React from "react"
import { FormikHelpers } from "formik"
import { useToast } from "~/utils/Toast"
import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"

import { apiSponsorships, apiSponsorshipAreas } from "~/routes"
import CatchModal from "~/library/CatchModal"
import SponsorshipForm from "~/components/Sponsorship/SponsorshipForm"
import { Area, AreaType } from "~/types/sponsorship/Area"

interface NewSponsorshipProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (areaType: AreaType) => void
  areaInfo?: {
    areaType: string
    areaId?: string
    cellDate?: string
  } | null
  areas?: Area[]
  isLoadingAreas?: boolean
}

const NewSponsorship: React.FC<NewSponsorshipProps> = ({
  isOpen,
  onClose,
  onSuccess,
  areaInfo,
}) => {
  const { venueSlug } = getAppConfig()
  const toast = useToast()

  const areasParams = {
    "q[s]": "area_type desc",
    ...(areaInfo?.areaType && { "q[area_type_eq]": areaInfo.areaType }),
  }

  const { data: areas, isLoading: isLoadingAreas } = useFetchResource<
    { sponsorshipAreas: Area[] },
    "sponsorshipAreas"
  >(apiSponsorshipAreas.index.path({ venueSlug }), "sponsorshipAreas", areasParams)

  const handleSubmit = (formValues: any, helpers: FormikHelpers<any>) => {
    const data = {
      venue_slug: venueSlug,
      sponsorship: {
        startOn: formValues.startOn,
        endOn: formValues.endOn,
        areaId: formValues.areaId,
        companyId: formValues.companyId,
        priceCents: parseInt(formValues.priceCents),
        quantity: formValues.quantity ? parseInt(formValues.quantity) : null,
        areaType: areaInfo?.areaType,
        ...(formValues.companyAttributes.name && {
          companyAttributes: formValues.companyAttributes,
        }),
      },
    }

    const req = apiSponsorships.create({ data })

    req
      .then((res) => {
        toast("Sponsorship created successfully", "success")
        onSuccess(res.sponsorship.areaType)
        onClose()
      })
      .catch((res) => {
        toast(`${res.body?.errors?.join(", ") || "Please try again"}`, "danger")
      })
      .finally(() => {
        helpers.setSubmitting(false)
      })
  }

  return (
    <CatchModal isOpen={isOpen} onClose={onClose} title="New Sponsorship" size="lg">
      <SponsorshipForm
        onSubmit={handleSubmit}
        onCancel={onClose}
        areas={areas || []}
        isLoadingAreas={isLoadingAreas}
        areaInfo={areaInfo}
      />
    </CatchModal>
  )
}

export default NewSponsorship
